/**
 * Dashboard Layout
 * Main layout with Sidebar + Topbar
 */

import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { vendorApi } from '@/lib/api';
import { useVendorStore } from '@/store/vendor';
import { wsClient } from '@/lib/ws';
import { WS_CHANNELS } from '@/lib/constants';
import { useOrdersStore } from '@/store/orders';
import { logger } from '@/lib/logger';
import Sidebar from '@/components/Sidebar';
import Topbar from '@/components/Topbar';
import StatusBanner from '@/components/StatusBanner';
import { isFeatureEnabled } from '@/lib/featureFlags';
import { useVendorHeartbeat } from '@/hooks/useVendorHeartbeat';

export default function Dashboard() {
  const { user, vendor, setVendor } = useAuthStore();
  const { setStats } = useVendorStore();
  const { addNewOrder, updateOrderStatus } = useOrdersStore();

  // Send periodic heartbeats to keep vendor online
  useVendorHeartbeat();

  useEffect(() => {
    // Load vendor profile if not loaded
    if (user && !vendor) {
      vendorApi
        .getProfile()
        .then((profile) => {
          setVendor(profile);
          logger.info('Vendor profile loaded', { vendorId: profile.id });
        })
        .catch((error) => {
          logger.error('Failed to load vendor profile', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    // Load vendor stats separately
    if (!vendor) return;
    
    vendorApi
      .getStats()
      .then((stats) => {
        setStats(stats);
      })
      .catch((error) => {
        logger.error('Failed to load vendor stats', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    // Connect WebSocket if dashboard is enabled
    if (!isFeatureEnabled('vendorDashboard')) {
      logger.warn('Vendor dashboard disabled, skipping WebSocket connection');
      return;
    }

    wsClient.connect();

    // Subscribe to vendor orders channel
    const handleOrderUpdate = (data: any) => {
      logger.info('Order update received', { orderId: data.orderId, type: data.type });

      if (data.type === 'new') {
        addNewOrder(data.order);
      } else if (data.type === 'status_change') {
        updateOrderStatus(data.orderId, data.status);
      }
    };

    // Subscribe to alerts channel
    const handleAlert = (data: any) => {
      logger.info('System alert received', { alertType: data.alert_type, severity: data.severity });

      // Show browser notification if supported
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('System Alert', {
          body: data.message,
          icon: '/icon-192.png',
          tag: data.id,
        });
      }

      // You can also show a toast notification here using a toast library
      // For now, just log it
      logger.info('Alert notification displayed', { alertId: data.id });
    };

    wsClient.subscribe(WS_CHANNELS.ORDERS_VENDOR, handleOrderUpdate);
    wsClient.subscribe(WS_CHANNELS.ALERTS, handleAlert);

    // Request notification permission on first load
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      wsClient.unsubscribe(WS_CHANNELS.ORDERS_VENDOR, handleOrderUpdate);
      wsClient.unsubscribe(WS_CHANNELS.ALERTS, handleAlert);
      wsClient.disconnect();
    };
  }, [addNewOrder, updateOrderStatus]);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <Topbar />

        {/* Status Banner (Sprint 27) */}
        <StatusBanner vendor={vendor} />

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

